/**
 * ShiftEventCodeCard — Iter-411k.
 *
 * Compact card that displays the onsite event code for a shift, plus
 * a "Rotate" button. Only mounts for viewers with visibility (the
 * backend `_can_see_event_code` guard returns 403 otherwise and we
 * hide the card silently).
 *
 * Usage: drop this on any admin/client/agency/onsite-lead-facing shift
 * detail page.
 *
 *   <ShiftEventCodeCard shiftId={shift.id} onsiteLeadUserId={shift.onsite_lead_user_id} />
 *
 * The code is intentionally rendered in a large, easy-to-read mono
 * font — this is what the onsite lead will read aloud to a talent
 * whose GPS + QR both failed.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { QrCode, RefreshCcw, Copy, Loader2 } from 'lucide-react';
import api from '../utils/api';

export default function ShiftEventCodeCard({ shiftId, className = '' }) {
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState(null);
  const [rotating, setRotating] = useState(false);
  const [hidden, setHidden] = useState(false);
  // Iter-411p — inline unavailable state (no toasts). Event Code is a
  // peripheral display; its failure must never look like a shift-save
  // failure or block admins editing shifts.
  const [loadError, setLoadError] = useState(null);

  const load = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`/shifts/${shiftId}/event-code`);
      setCode(res.data);
    } catch (e) {
      // 403 means this viewer isn't allowed to see it — hide silently.
      if (e?.response?.status === 403) {
        setHidden(true);
      } else {
        // Iter-411p — NEVER toast. This card auto-mounts on every shift
        // detail render (including immediately after a successful save)
        // so a toast next to a save button would look like the save
        // itself failed. Render inline "temporarily unavailable" with
        // a retry, and log the detail for admins.
        const detail = e?.response?.data?.detail || e?.message || 'temporarily_unavailable';
        setLoadError(String(detail).slice(0, 240));
        console.warn('[ShiftEventCodeCard] load failed:', detail);
      }
    } finally {
      setLoading(false);
    }
  }, [shiftId]);

  useEffect(() => { load(); }, [load]);

  const rotate = async () => {
    setRotating(true);
    try {
      const res = await api.post(`/shifts/${shiftId}/event-code/regenerate`);
      setCode((prev) => ({ ...(prev || {}), event_code: res.data.event_code }));
      toast.success('Event code rotated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Rotate failed');
    } finally {
      setRotating(false);
    }
  };

  const copy = async () => {
    if (!code?.event_code) return;
    try {
      await navigator.clipboard.writeText(code.event_code);
      toast.success('Code copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  if (hidden) return null;

  return (
    <div
      data-testid="shift-event-code-card"
      className={`rounded-xl border border-cyan-200 bg-gradient-to-br from-cyan-50 to-white p-4 ${className}`}
    >
      <div className="flex items-start justify-between mb-2">
        <div>
          <div className="flex items-center gap-2 text-[11px] uppercase font-semibold text-cyan-800 tracking-wide">
            <QrCode className="w-3.5 h-3.5" />
            Onsite Event Code
          </div>
          <div className="text-[11px] text-cyan-700 mt-0.5">
            Read this to any talent whose GPS or QR check-in fails.
          </div>
        </div>
        <button
          type="button"
          onClick={rotate}
          disabled={rotating}
          className="text-xs text-cyan-700 hover:text-cyan-900 inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="event-code-rotate-btn"
        >
          {rotating ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCcw className="w-3 h-3" />}
          Rotate
        </button>
      </div>
      {loading ? (
        <div className="h-14 flex items-center justify-center text-cyan-500">
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : loadError ? (
        <div
          className="flex items-center justify-between gap-2 rounded-md border border-cyan-200 bg-white/60 px-3 py-2"
          data-testid="event-code-unavailable"
        >
          <div className="text-xs text-cyan-800">
            Event code temporarily unavailable.
            <span className="block text-[10px] text-cyan-600 mt-0.5">
              Saving the shift is not affected — talent can still check in via
              GPS, selfie, or the onsite fallback.
            </span>
          </div>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900 border border-cyan-300 rounded px-2 py-1"
            data-testid="event-code-retry-btn"
          >
            <RefreshCcw className="w-3 h-3" /> Retry
          </button>
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2">
          <div
            data-testid="event-code-value"
            className="font-mono text-3xl tracking-[0.35em] font-bold text-cyan-900 select-all"
          >
            {code?.event_code || '——————'}
          </div>
          <button
            type="button"
            onClick={copy}
            className="inline-flex items-center gap-1 text-xs text-cyan-700 hover:text-cyan-900 border border-cyan-300 rounded px-2 py-1"
            data-testid="event-code-copy-btn"
          >
            <Copy className="w-3 h-3" /> Copy
          </button>
        </div>
      )}
    </div>
  );
}
